import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Warehouse, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { ProcurementItem } from '@/types/database/procurement-items';
import { formatCurrency } from '@/lib/currency';

interface ItemWarehouseReceivingSectionProps {
  items: ProcurementItem[];
  currency?: string;
  averagePricePerUnit?: number;
}

export const ItemWarehouseReceivingSection: React.FC<ItemWarehouseReceivingSectionProps> = ({
  items,
  currency = 'ETB',
  averagePricePerUnit = 0
}) => {
  const [isOpen, setIsOpen] = React.useState(true);

  // Filter items that have warehouse data
  const itemsWithWarehouse = items.filter(item => item.item_warehouse);

  if (itemsWithWarehouse.length === 0) {
    return null;
  }

  // Separate rejected and non-rejected items
  const rejectedItems = itemsWithWarehouse.filter(item => item.item_quality?.is_rejected);
  const activeItems = itemsWithWarehouse.filter(item => !item.item_quality?.is_rejected);

  // Calculate totals (only for non-rejected items)
  const totals = activeItems.reduce((acc, item) => {
    const warehouse = item.item_warehouse!;
    const compensation = item.item_quality?.compensation_kg || 0;
    const netPayable = Math.max(0, warehouse.net_payable_kg);
    const itemPrice = item.price_per_unit || averagePricePerUnit;
    const itemAmount = netPayable * itemPrice;
    
    return {
      totalExpected: acc.totalExpected + (item.quantity || 0),
      totalReceived: acc.totalReceived + warehouse.received_quantity_kg,
      totalCompensation: acc.totalCompensation + compensation,
      totalNetPayable: acc.totalNetPayable + netPayable,
      totalAmount: acc.totalAmount + itemAmount,
    };
  }, { totalExpected: 0, totalReceived: 0, totalCompensation: 0, totalNetPayable: 0, totalAmount: 0 });

  const hasVariance = Math.abs(totals.totalExpected - totals.totalReceived) > 0.01;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-4">
      <CollapsibleTrigger className="flex items-center gap-2 w-full text-left p-2 hover:bg-muted/50 rounded-lg transition-colors">
        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        <Warehouse className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">Per-Item Warehouse Receiving</span>
        <Badge variant="secondary" className="ml-auto">{itemsWithWarehouse.length} items</Badge>
        {rejectedItems.length > 0 && (
          <Badge variant="destructive">{rejectedItems.length} rejected</Badge>
        )}
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-3 space-y-3">
        {/* Rejected items first */}
        {rejectedItems.map((item, index) => (
          <Card key={item.id} className="border-l-4 border-l-destructive bg-destructive/5">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">#{index + 1}</span>
                  <span className="font-medium line-through text-muted-foreground">{item.product_name}</span>
                </div>
                <Badge variant="destructive" className="flex items-center gap-1">
                  <XCircle className="h-3 w-3" />
                  Rejected at Quality
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div className="bg-muted/50 rounded p-2">
                  <p className="text-muted-foreground text-xs">Ordered</p>
                  <p className="font-medium line-through">{(item.quantity || 0).toFixed(2)} kg</p>
                </div>
                <div className="bg-muted/50 rounded p-2">
                  <p className="text-muted-foreground text-xs">Received</p>
                  <p className="font-medium">0.00 kg</p>
                </div>
                <div className="bg-muted/50 rounded p-2">
                  <p className="text-muted-foreground text-xs">Compensation</p>
                  <p className="font-medium">{(item.quantity || 0).toFixed(2)} kg</p>
                </div>
                <div className="bg-muted/50 rounded p-2">
                  <p className="text-muted-foreground text-xs">Net Payable</p>
                  <p className="font-semibold">0.00 kg</p>
                </div>
                <div className="bg-muted/50 rounded p-2">
                  <p className="text-muted-foreground text-xs">Amount to Pay</p>
                  <p className="font-semibold">{formatCurrency(0, currency)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Active items */}
        {activeItems.map((item, index) => {
          const warehouse = item.item_warehouse!;
          const compensation = item.item_quality?.compensation_kg || 0;
          const expectedQty = item.quantity || 0;
          const receivedQty = warehouse.received_quantity_kg;
          const variance = expectedQty - receivedQty;
          const hasItemVariance = Math.abs(variance) > 0.01;
          const itemPrice = item.price_per_unit || averagePricePerUnit;
          const netPayable = Math.max(0, warehouse.net_payable_kg);
          const itemAmount = netPayable * itemPrice;
          
          return (
            <Card key={item.id} className={`border-l-4 ${hasItemVariance ? 'border-l-amber-500' : 'border-l-emerald-500'}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">#{rejectedItems.length + index + 1}</span>
                    <span className="font-medium">{item.product_name}</span>
                    {item.quality_grade && (
                      <Badge variant="outline" className="text-xs">
                        Grade {item.quality_grade}
                      </Badge>
                    )}
                  </div>
                  {hasItemVariance ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Variance: {variance.toFixed(2)} kg
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Matched
                    </Badge>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                  <div className="bg-muted/50 rounded p-2">
                    <p className="text-muted-foreground text-xs">Expected</p>
                    <p className="font-medium">{expectedQty.toFixed(2)} kg</p>
                  </div>
                  <div className={`rounded p-2 ${hasItemVariance ? 'bg-amber-50' : 'bg-muted/50'}`}>
                    <p className="text-muted-foreground text-xs">Received</p>
                    <p className={`font-medium ${hasItemVariance ? 'text-amber-700' : ''}`}>
                      {receivedQty.toFixed(2)} kg
                    </p>
                  </div>
                  <div className="bg-muted/50 rounded p-2">
                    <p className="text-muted-foreground text-xs">Compensation</p>
                    <p className="font-medium">{compensation.toFixed(2)} kg</p>
                  </div>
                  <div className="bg-primary/10 rounded p-2">
                    <p className="text-muted-foreground text-xs">Net Payable</p>
                    <p className="font-semibold text-primary">{netPayable.toFixed(2)} kg</p>
                  </div>
                  <div className="bg-green-50 rounded p-2">
                    <p className="text-muted-foreground text-xs">Amount to Pay</p>
                    <p className="font-semibold text-green-700">{formatCurrency(itemAmount, currency)}</p>
                  </div>
                </div>
                
                {warehouse.warehouse_notes && (
                  <p className="mt-2 text-sm text-muted-foreground italic">
                    Note: {warehouse.warehouse_notes}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
        
        {/* Totals Summary */}
        <Card className={`border-2 ${hasVariance ? 'bg-amber-50/50 border-amber-200' : 'bg-muted/30'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Total ({activeItems.length} active items)</span>
                {hasVariance && (
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Variance Detected
                  </Badge>
                )}
                {rejectedItems.length > 0 && (
                  <Badge variant="secondary" className="text-muted-foreground">
                    {rejectedItems.length} rejected = {formatCurrency(0, currency)}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-6 text-sm flex-wrap">
                <div className="text-center">
                  <p className="text-muted-foreground text-xs">Expected</p>
                  <p className="font-medium">{totals.totalExpected.toFixed(2)} kg</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground text-xs">Received</p>
                  <p className={`font-medium ${hasVariance ? 'text-amber-700' : ''}`}>
                    {totals.totalReceived.toFixed(2)} kg
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground text-xs">Compensation</p>
                  <p className="font-medium">{totals.totalCompensation.toFixed(2)} kg</p>
                </div>
                <div className="text-center">
                  <p className="text-muted-foreground text-xs">Net Payable</p>
                  <p className="font-semibold text-primary">{totals.totalNetPayable.toFixed(2)} kg</p>
                </div>
                <div className="text-center bg-green-100 rounded px-3 py-1">
                  <p className="text-muted-foreground text-xs">Total Amount</p>
                  <p className="font-bold text-green-700">{formatCurrency(totals.totalAmount, currency)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default ItemWarehouseReceivingSection;
